import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { UncoveredOrg } from '../lib/memberships';
import { DeniedState } from './DeniedState';
import { buttonClass } from './ui/Button';

/**
 * `/org/<org>` answered 403 for this session. Two different answers:
 *
 *   - `uncovered` is null: the URL names an org the user is not a member of (a
 *     typo, a stale bookmark, a removed membership, a renamed org);
 *   - `uncovered` is set: the user IS a member, but this sign-in does not cover
 *     the org (a session pinned to another org's SSO). It names what the sign-in
 *     covers and, when the server named one, the org's own sign-in leg, which
 *     adds the org to this session and lands back on this page.
 *
 * Rendered by `<OrgScope/>` INSIDE the shell, so the rail and the Back button
 * still work, and the bare `/` exit lands in the session's own org
 * (`LegacyRedirect`).
 */
export function OrgDenied(props: {
  readonly org: string;
  readonly error: unknown;
  readonly uncovered: UncoveredOrg | null;
}): JSX.Element {
  const message = (): string => {
    const u = props.uncovered;
    if (u === null) return t('org_scope.not_member', { org: props.org });
    return u.covers.length === 0
      ? t('org_scope.not_covered', { org: props.org })
      : t('org_scope.covers_only', { org: props.org, covers: u.covers.join(', ') });
  };
  return (
    <section class="page">
      <DeniedState error={props.error} message={message()} />
      {/* DeniedState's own action row class, so the two exits sit as one row. */}
      <div class="error-state__actions">
        <Show when={props.uncovered?.signIn} keyed>
          {(href) => (
            <a class={buttonClass()} href={href}>
              {t('org_scope.sign_in', { org: props.org })}
            </a>
          )}
        </Show>
        <a
          class={buttonClass({ variant: props.uncovered?.signIn ? 'outline' : 'primary' })}
          href="/"
        >
          {t('org_scope.exit')}
        </a>
      </div>
    </section>
  );
}
